import { useQuery } from "@tanstack/react-query";
import type { Location } from "@components/ui/location_input";
import { CompletionStatusType } from "@utils/completion_status";
import { DoNotSellType } from "@utils/do_not_sell";
import { Education } from "@components/quick_apply/education_section";
import { Employment } from "@components/quick_apply/employments";
import { Websites } from "@components/quick_apply/websites";
import { SelfIdentificationProps } from "@/utils/self_identification";

export interface FileState {
  id?: string;
  file: File | null;
  filename: string;
  url: string;
  content_type: string;
}

export interface ProfileData {
  email: string;
  firstName: string | null;
  lastName: string | null;
  isClearVerified: boolean;
  hasPendingClearVerificationTask: boolean;
  phoneNumber: string | null;
  preferredName: string | null;
  location: Location | null;
  resume: FileState | null;
  educations: Education[];
  employments: Employment[];
  websites: Websites;
  accountCompletion: CompletionStatusType;
  quickApplyCompletion: CompletionStatusType;
  workPreferencesCompletion: CompletionStatusType;
  workPreferences: string[];
  doNotSell: DoNotSellType | null;
  paths: {
    userPath: string;
    profilePath: string;
    quickApplyPath: string;
    educationPath: string;
    doNotSellPath: string;
    resumeUploadPath: string;
  };
  selfIdentification: SelfIdentificationProps;
}

const useProfile = ({ mock }: { mock?: boolean } = {}) => {
  if (mock) {
    return { data: {} as ProfileData, isLoading: false, error: null };
  }
  const fetchProfile = async (): Promise<ProfileData> => {
    const response = await fetch("/profiles/data.json");
    if (!response.ok) {
      throw new Error("Failed to fetch profile data");
    }
    return response.json();
  };

  return useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
    staleTime: 0,
  });
};

export default useProfile;
